import React, { useState, useEffect, useCallback } from 'react';
import { getHubComparison } from '../api';
import {
  TrendUp, TrendDown, Minus, ShieldCheck, CheckCircle,
  Warning, SpinnerGap, ArrowLeft, ChartLineUp
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const FW_COLORS = {
  'IFRS S2': { bg: 'bg-blue-500/15', text: 'text-blue-600', bar: '#3b82f6' },
  'GRI': { bg: 'bg-emerald-500/15', text: 'text-emerald-600', bar: '#10b981' },
  'SASB': { bg: 'bg-violet-500/15', text: 'text-violet-600', bar: '#8b5cf6' },
  'TNFD': { bg: 'bg-teal-500/15', text: 'text-teal-600', bar: '#14b8a6' },
  'CDP': { bg: 'bg-amber-500/15', text: 'text-amber-600', bar: '#f59e0b' },
  'CBAM': { bg: 'bg-orange-500/15', text: 'text-orange-600', bar: '#f97316' },
};

const PERIOD_ORDER = ['FY2024', 'H2 2024', 'FY2025', 'Q4 2025', 'FY2026', 'Q1 2026'];

function sortPeriods(periods) {
  return periods.sort((a, b) => PERIOD_ORDER.indexOf(a) - PERIOD_ORDER.indexOf(b));
}

function TrendIcon({ trend }) {
  if (trend === 'up') return <TrendUp size={12} weight="bold" className="text-emerald-500" />;
  if (trend === 'down') return <TrendDown size={12} weight="bold" className="text-emerald-500" />;
  return <Minus size={12} className="text-tertiary" />;
}

function ChartTooltipContent({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border rounded-lg p-3 shadow-lg text-xs font-body">
      <p className="font-semibold text-text-primary mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-text-secondary">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold text-text-primary">{p.value}%</span>
        </p>
      ))}
    </div>
  );
}

export default function ComparisonView({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFw, setSelectedFw] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHubComparison();
      setData(res.data);
    } catch (err) {
      console.error('Comparison data fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="comparison-loading">
        <SpinnerGap size={32} className="text-primary animate-spin" />
      </div>
    );
  }

  const { compliance_timeline, metric_timeline, available_periods } = data;
  const sortedPeriods = sortPeriods([...available_periods]);

  // Build compliance chart data
  const complianceChartData = sortedPeriods.map(period => {
    const entry = { period };
    compliance_timeline.forEach(fw => {
      const pd = fw.periods[period];
      if (pd) entry[fw.framework] = pd.completeness;
    });
    return entry;
  });

  // Filter metrics by framework
  const filteredMetrics = selectedFw === 'all'
    ? metric_timeline
    : metric_timeline.filter(m => m.framework === selectedFw);

  // Group metrics by framework
  const metricsByFw = {};
  filteredMetrics.forEach(m => {
    if (!metricsByFw[m.framework]) metricsByFw[m.framework] = [];
    metricsByFw[m.framework].push(m);
  });

  return (
    <div className="space-y-5" data-testid="comparison-view">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-1.5 px-3 py-2 border border-border rounded-lg text-xs font-body font-medium text-text-secondary hover:text-text-primary hover:border-tertiary transition-all"
              data-testid="comparison-back"
            >
              <ArrowLeft size={14} /> Back to Hub
            </button>
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-text-primary tracking-tight">
              Framework Comparison
            </h1>
          </div>
          <p className="font-body text-sm text-text-secondary mt-1 ml-[106px]">
            Track metrics across FY2024 → FY2025 → Q1 2026 for all sustainability frameworks
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/10 rounded-lg">
          <ChartLineUp size={16} className="text-primary" />
          <span className="text-xs font-body font-semibold text-primary">{sortedPeriods.length} periods &middot; {compliance_timeline.length} frameworks</span>
        </div>
      </div>

      {/* Compliance Completeness Chart */}
      <div className="bg-surface border border-border rounded-xl p-5" data-testid="compliance-chart">
        <h2 className="font-heading text-base font-semibold text-text-primary mb-4">Disclosure Completeness Across Periods (%)</h2>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={complianceChartData} barGap={2} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#2E3A28" />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#A3A380' }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#A3A380' }} />
              <Tooltip content={<ChartTooltipContent />} />
              {compliance_timeline.map(fw => (
                <Bar
                  key={fw.framework}
                  dataKey={fw.framework}
                  name={fw.framework}
                  fill={FW_COLORS[fw.framework]?.bar || '#888'}
                  radius={[3, 3, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap gap-4 mt-3 justify-center">
          {compliance_timeline.map(fw => (
            <div key={fw.framework} className="flex items-center gap-1.5 text-xs font-body text-text-secondary">
              <div className="w-3 h-3 rounded" style={{ background: FW_COLORS[fw.framework]?.bar || '#888' }} />
              {fw.framework}
            </div>
          ))}
        </div>
      </div>

      {/* Compliance Detail Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="compliance-cards">
        {compliance_timeline.map(fw => {
          const periods = Object.entries(fw.periods).sort(([a], [b]) => PERIOD_ORDER.indexOf(a) - PERIOD_ORDER.indexOf(b));
          const latest = periods[periods.length - 1]?.[1];
          const earliest = periods[0]?.[1];
          const delta = latest && earliest ? latest.completeness - earliest.completeness : 0;
          const fwColor = FW_COLORS[fw.framework] || { bg: 'bg-slate-400/15', text: 'text-slate-600' };

          return (
            <div key={fw.framework} className="bg-surface border border-border rounded-xl p-4" data-testid={`compliance-card-${fw.framework}`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`px-2.5 py-1 rounded-lg text-xs font-heading font-bold ${fwColor.bg} ${fwColor.text}`}>{fw.framework}</span>
                <span className={`text-xs font-body font-bold ${delta > 0 ? 'text-emerald-500' : delta < 0 ? 'text-red-500' : 'text-tertiary'}`}>
                  {delta > 0 ? '+' : ''}{delta}pp
                </span>
              </div>
              <div className="space-y-2">
                {periods.map(([period, pd]) => (
                  <div key={period} className="flex items-center gap-3">
                    <span className="text-[10px] font-body font-semibold text-tertiary w-16 flex-shrink-0">{period}</span>
                    <div className="flex-1 h-2 bg-background rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pd.completeness}%`, background: FW_COLORS[fw.framework]?.bar || '#888' }}
                      />
                    </div>
                    <span className="text-[11px] font-body font-bold text-text-primary w-10 text-right">{pd.completeness}%</span>
                    <div className="w-5 flex justify-center">
                      {pd.verified ? <CheckCircle size={12} weight="fill" className="text-emerald-500" /> : <Warning size={12} className="text-tertiary" />}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-border/50 flex items-center justify-between">
                <span className="text-[10px] font-body text-text-secondary">Evidence Coverage</span>
                <span className="text-[11px] font-body font-semibold text-text-primary">{latest?.evidence || 0}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Metric Timeline Table */}
      <div className="bg-surface border border-border rounded-xl p-5" data-testid="metric-timeline">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} weight="duotone" className="text-primary" />
            <h2 className="font-heading text-base font-semibold text-text-primary">Key Metrics Over Time</h2>
          </div>
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={() => setSelectedFw('all')}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-body font-medium transition-all ${selectedFw === 'all' ? 'bg-primary text-white' : 'bg-background border border-border text-text-secondary hover:border-primary hover:text-primary'}`}
              data-testid="comparison-filter-all"
            >
              All
            </button>
            {compliance_timeline.map(fw => (
              <button
                key={fw.framework}
                onClick={() => setSelectedFw(fw.framework)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-body font-medium transition-all ${selectedFw === fw.framework ? 'bg-primary text-white' : 'bg-background border border-border text-text-secondary hover:border-primary hover:text-primary'}`}
                data-testid={`comparison-filter-${fw.framework.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {fw.framework}
              </button>
            ))}
          </div>
        </div>

        {Object.entries(metricsByFw).map(([fw, metrics]) => {
          const fwColor = FW_COLORS[fw] || { bg: 'bg-slate-400/15', text: 'text-slate-600' };
          return (
            <div key={fw} className="mb-6 last:mb-0">
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-2 py-0.5 rounded text-[10px] font-body font-bold ${fwColor.bg} ${fwColor.text}`}>{fw}</span>
                <div className="h-px flex-1 bg-border/50" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-body">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-4 text-[10px] font-semibold uppercase tracking-wider text-tertiary w-48">Metric</th>
                      {sortedPeriods.map(p => (
                        <th key={p} className="text-center py-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-tertiary min-w-[110px]">{p}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.map((m, i) => (
                      <tr key={i} className="border-b border-border/30 hover:bg-background/50">
                        <td className="py-2.5 pr-4 font-medium text-text-primary text-sm">{m.metric}</td>
                        {sortedPeriods.map(period => {
                          const pd = m.periods[period];
                          if (!pd) return <td key={period} className="text-center py-2.5 px-3 text-tertiary">—</td>;
                          return (
                            <td key={period} className="text-center py-2.5 px-3">
                              <span className="font-heading text-sm font-bold text-text-primary">{pd.value}</span>
                              {pd.change && (
                                <span className="flex items-center justify-center gap-1 mt-0.5">
                                  <TrendIcon trend={pd.trend} />
                                  <span className={`text-[10px] font-medium ${
                                    pd.trend === 'up' || pd.trend === 'down' ? 'text-emerald-500' : 'text-tertiary'
                                  }`}>{pd.change}</span>
                                </span>
                              )}
                              {pd.baseline && (
                                <span className="text-[9px] text-tertiary italic block">baseline</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
